import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { ArrowRight, ArrowLeft, SkipForward } from "lucide-react";
import { SignupStepOne } from "./SignupStepOne";
import { SignupStepTwo } from "./SignupStepTwo";
import { SignupStepThree } from "./SignupStepThree";
import { AccountType } from "@/types/payment";

interface SignupFormProps {
  step: number;
  isLoading: boolean;
  name: string;
  setName: (name: string) => void;
  email: string;
  setEmail: (email: string) => void;
  username: string;
  setUsername: (username: string) => void;
  password: string;
  setPassword: (password: string) => void;
  usernameStatus: "typing" | "checking" | "available" | "taken";
  avatars: string[];
  selectedAvatar: string;
  setSelectedAvatar: (url: string) => void;
  avatarTab: "preset" | "custom";
  setAvatarTab: (tab: "preset" | "custom") => void;
  customAvatarPreview: string;
  clearCustomAvatar: () => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
  handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  paymentTab: "bank" | "paypay";
  setPaymentTab: (tab: "bank" | "paypay") => void;
  bankName: string;
  setBankName: (name: string) => void;
  branchName: string;
  setBranchName: (name: string) => void;
  accountType: AccountType;
  setAccountType: (type: AccountType) => void;
  accountNumber: string;
  setAccountNumber: (number: string) => void;
  accountHolderName: string;
  setAccountHolderName: (name: string) => void;
  paypayUsername: string;
  setPaypayUsername: (username: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  handlePrevStep: () => void;
  handleSkipPaymentSetup: (e: React.MouseEvent) => void;
  switchToLogin: () => void;
}

export const SignupForm = ({
  step,
  isLoading,
  name,
  setName,
  email,
  setEmail,
  username,
  setUsername,
  password,
  setPassword,
  usernameStatus,
  avatars,
  selectedAvatar,
  setSelectedAvatar,
  avatarTab,
  setAvatarTab,
  customAvatarPreview,
  clearCustomAvatar,
  fileInputRef,
  handleFileChange,
  paymentTab,
  setPaymentTab,
  bankName,
  setBankName,
  branchName,
  setBranchName,
  accountType,
  setAccountType,
  accountNumber,
  setAccountNumber,
  accountHolderName,
  setAccountHolderName,
  paypayUsername,
  setPaypayUsername,
  onSubmit,
  handlePrevStep,
  handleSkipPaymentSetup,
  switchToLogin
}: SignupFormProps) => {
  const { t } = useTranslation();

  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <SignupStepOne
            name={name}
            setName={setName}
            email={email}
            setEmail={setEmail}
            username={username}
            setUsername={setUsername}
            password={password}
            setPassword={setPassword}
            usernameStatus={usernameStatus}
          />
        );
      case 2:
        return (
          <SignupStepTwo
            avatars={avatars}
            selectedAvatar={selectedAvatar}
            setSelectedAvatar={setSelectedAvatar}
            avatarTab={avatarTab}
            setAvatarTab={setAvatarTab}
            customAvatarPreview={customAvatarPreview}
            clearCustomAvatar={clearCustomAvatar}
            fileInputRef={fileInputRef}
            handleFileChange={handleFileChange}
          />
        );
      case 3:
        return (
          <SignupStepThree
            paymentTab={paymentTab}
            setPaymentTab={setPaymentTab}
            bankName={bankName}
            setBankName={setBankName}
            branchName={branchName}
            setBranchName={setBranchName}
            accountType={accountType}
            setAccountType={setAccountType}
            accountNumber={accountNumber}
            setAccountNumber={setAccountNumber}
            accountHolderName={accountHolderName}
            setAccountHolderName={setAccountHolderName}
            paypayUsername={paypayUsername}
            setPaypayUsername={setPaypayUsername}
          />
        );
      default:
        return null;
    }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      {renderStepContent()}
      
      <Button 
        type="submit" 
        className="w-full h-12 text-base font-medium" 
        disabled={isLoading}
      >
        {isLoading ? (
          <div className="flex items-center">
            <div className="h-5 w-5 mr-2 animate-spin rounded-full border-2 border-white border-r-transparent"></div>
            {step < 3 ? t('auth.creating') : t('auth.creating')}
          </div>
        ) : (
          <>
            {step < 3 ? (
              <div className="flex items-center">
                {t('auth.next')}
                <ArrowRight className="ml-2 h-5 w-5" />
              </div>
            ) : (
              t('auth.createAccount')
            )}
          </>
        )}
      </Button>
      
      {step === 3 && (
        <Button 
          type="button" 
          variant="outline" 
          className="w-full mt-2" 
          onClick={handleSkipPaymentSetup}
        >
          <SkipForward className="mr-2 h-4 w-4" />
          {t('common.skip')}
        </Button>
      )}
      
      {step > 1 && (
        <Button 
          type="button" 
          variant="ghost" 
          className="w-full mt-2" 
          onClick={handlePrevStep}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('auth.back')}
        </Button>
      )}
      
      <div className="flex items-center justify-center w-full mt-4">
        <Button 
          type="button" 
          variant="outline" 
          className="text-sm py-2 px-4 w-full max-w-xs transition-all duration-200 hover:scale-105"
          onClick={switchToLogin}
          size="sm"
        >
          {t('auth.alreadyAccount')}
        </Button>
      </div>
    </form>
  );
};
